// Central, typed access to build-time configuration.
//
// Everything the app needs to identify itself (appId, storeId), reach the
// right Firebase pool, and know whether it's in test mode comes from here.
// Nothing else in the app should read `import.meta.env` directly.

const raw = import.meta.env as Record<string, string | undefined>;

function str(key: string, fallback = ""): string {
  const v = raw[key];
  return v === undefined || v === "" ? fallback : v;
}

function bool(key: string, fallback = false): boolean {
  const v = raw[key];
  if (v === undefined) return fallback;
  return v === "true" || v === "1";
}

function num(key: string, fallback: number): number {
  const v = raw[key];
  if (v === undefined || v === "") return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export interface AppConfig {
  appId: string;
  storeId: string;
  storeName: string;
  storeAddress: string;
  storePhone: string;
  /** When true, orders submit with test_mode=true and never hit the live KDS. */
  testMode: boolean;
  currencySymbol: string;
  currencyCode: string;
  timezone: string;
  functionsRegion: string;
  createPosOrderFn: string;
  /** When true, Kitchen fire also creates the order on the cloud KDS
   *  (requires the backend fire_only / mark-paid support — see README). */
  kdsFireOnHold: boolean;
  markPosOrderPaidFn: string;
  cancelPosOrderFn: string;
  /** Cancel a paid order (void; online + POS). DineCentral callable. */
  cancelOrderFn: string;
  /** Refund a paid POS order — partial or full, ledger-based, no Stripe. */
  refundPosOrderFn: string;
  /** Orders worker (Neon) base URL — Transactions tab pulls history from it. */
  ordersApiUrl: string;
  /** Store has DinePlatform online ordering — shows Ordering Settings etc. */
  onlineOrderingSite: boolean;
  /** When true, ALL settings stay device-local (legacy behaviour). When false,
   *  site-wide settings (staff, service & charges, customers, receipt design)
   *  are presented separately — they'll live in the cloud (client center). */
  localOnlyPosSettings: boolean;
  /** Store takes card payments through an integrated terminal — shows the
   *  Card payments settings section. */
  integratedCardPayments: boolean;
  createPosTicketFn: string;
  updatePosTicketStatusFn: string;
  /** Where POS orders (and sign-in) go: legacy pool project or DineCentral. */
  orderBackend: "pool" | "dinecentral";
  /** Where the menu comes from: pool Firestore or the public JSON endpoint. */
  menuSource: "pool-firestore" | "public-json";
  menuJsonUrl: string;
  dinecentral: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
    functionsRegion: string;
  };
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
  };
  defaults: {
    tableCount: number;
    serviceChargePercent: number;
    taxPercent: number;
  };
  /** Filled at boot by src/config/runtime.ts (cloud-served / URL-configured builds). */
  hubAddresses?: { lan?: string; lanTls?: string; relay?: string; cloud?: string };
  runtime?: import("./runtime").RuntimeInfo;
}

export const config: AppConfig = {
  appId: str("VITE_APP_ID"),
  storeId: str("VITE_STORE_ID"),
  storeName: str("VITE_STORE_NAME", "My Store"),
  storeAddress: str("VITE_STORE_ADDRESS", ""),
  storePhone: str("VITE_STORE_PHONE", ""),
  testMode: bool("VITE_POS_TEST_MODE", true),
  currencySymbol: str("VITE_CURRENCY_SYMBOL", "£"),
  currencyCode: str("VITE_CURRENCY_CODE", "GBP"),
  timezone: str("VITE_TIMEZONE", "Europe/London"),
  functionsRegion: str("VITE_FUNCTIONS_REGION", "europe-west2"),
  createPosOrderFn: str("VITE_CREATE_POS_ORDER_FN", "createPOSOrder"),
  kdsFireOnHold: bool("VITE_KDS_FIRE_ON_HOLD", false),
  markPosOrderPaidFn: str("VITE_MARK_POS_ORDER_PAID_FN", "markPOSOrderPaid"),
  cancelPosOrderFn: str("VITE_CANCEL_POS_ORDER_FN", "cancelPOSOrder"),
  cancelOrderFn: str("VITE_CANCEL_ORDER_FN", "cancelOrderCallable"),
  refundPosOrderFn: str("VITE_REFUND_POS_ORDER_FN", "refundPOSOrder"),
  ordersApiUrl: str("VITE_ORDERS_API_URL", "https://dineplatform-orders.jotlumanagement.workers.dev"),
  onlineOrderingSite: bool("VITE_ONLINE_ORDERING_SITE", false),
  localOnlyPosSettings: bool("VITE_LOCAL_ONLY_POS_SETTINGS", false),
  integratedCardPayments: bool("VITE_INTEGRATED_CARD_PAYMENTS", false),
  createPosTicketFn: str("VITE_CREATE_POS_TICKET_FN", "createPOSTicket"),
  updatePosTicketStatusFn: str("VITE_UPDATE_POS_TICKET_STATUS_FN", "updatePOSTicketStatus"),
  orderBackend: (str("VITE_ORDER_BACKEND", "pool") === "dinecentral"
    ? "dinecentral"
    : "pool") as "pool" | "dinecentral",
  menuSource: (str("VITE_MENU_SOURCE", "pool-firestore") === "public-json"
    ? "public-json"
    : "pool-firestore") as "pool-firestore" | "public-json",
  menuJsonUrl: str(
    "VITE_MENU_JSON_URL",
    "https://www.dineplatform.com/r/{appId}/restaurants.json"
  ),
  dinecentral: {
    apiKey: str("VITE_DINECENTRAL_API_KEY"),
    authDomain: str("VITE_DINECENTRAL_AUTH_DOMAIN"),
    projectId: str("VITE_DINECENTRAL_PROJECT_ID"),
    storageBucket: str("VITE_DINECENTRAL_STORAGE_BUCKET"),
    messagingSenderId: str("VITE_DINECENTRAL_MESSAGING_SENDER_ID"),
    appId: str("VITE_DINECENTRAL_APP_ID"),
    measurementId: raw["VITE_DINECENTRAL_MEASUREMENT_ID"] || undefined,
    functionsRegion: str("VITE_DINECENTRAL_FUNCTIONS_REGION", "europe-west2"),
  },
  firebase: {
    apiKey: str("VITE_FIREBASE_API_KEY"),
    authDomain: str("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: str("VITE_FIREBASE_PROJECT_ID"),
    storageBucket: str("VITE_FIREBASE_STORAGE_BUCKET"),
    messagingSenderId: str("VITE_FIREBASE_MESSAGING_SENDER_ID"),
    appId: str("VITE_FIREBASE_APP_ID"),
    measurementId: raw["VITE_FIREBASE_MEASUREMENT_ID"] || undefined,
  },
  defaults: {
    tableCount: num("VITE_DEFAULT_TABLE_COUNT", 12),
    serviceChargePercent: num("VITE_DEFAULT_SERVICE_CHARGE_PERCENT", 0),
    taxPercent: num("VITE_DEFAULT_TAX_PERCENT", 0),
  },
};

export function poolFirebaseConfigured(): boolean {
  return !!(config.firebase.apiKey && config.firebase.projectId && config.firebase.appId);
}

/** Returns a list of missing-but-required config keys (for a startup check).
 *  What's required depends on the selected backend + menu source:
 *  - pool backend  → pool Firebase config
 *  - dinecentral   → DineCentral Firebase config
 *  - pool-firestore menu → pool Firebase config (even on the DC backend)
 */
export function missingConfig(): string[] {
  const missing: string[] = [];
  if (!config.appId) missing.push("VITE_APP_ID");
  if (!config.storeId) missing.push("VITE_STORE_ID");

  const needPool =
    config.orderBackend === "pool" || config.menuSource === "pool-firestore";
  if (needPool) {
    if (!config.firebase.apiKey) missing.push("VITE_FIREBASE_API_KEY");
    if (!config.firebase.projectId) missing.push("VITE_FIREBASE_PROJECT_ID");
    if (!config.firebase.appId) missing.push("VITE_FIREBASE_APP_ID");
  }
  if (config.orderBackend === "dinecentral") {
    if (!config.dinecentral.apiKey) missing.push("VITE_DINECENTRAL_API_KEY");
    if (!config.dinecentral.projectId) missing.push("VITE_DINECENTRAL_PROJECT_ID");
    if (!config.dinecentral.appId) missing.push("VITE_DINECENTRAL_APP_ID");
  }
  return missing;
}
